import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Icon, Chip, Button, RawHtml, AnchorLink } from 'platfi-ui-components';

import BuildingIcon from 'assets/svgs/building.svg';
import CertificateIcon from 'assets/svgs/certificate.svg';
import ChartIcon from 'assets/svgs/chart.svg';
import LockIcon from 'assets/svgs/lock.svg';
import UsersIcon from 'assets/svgs/users.svg';
import UserWarningIcon from 'assets/svgs/user-warning.svg';
import ClientsMonitor from 'assets/svgs/clients-monitor.svg';

import Modal from 'components/modal';
import withOpenIntercom from 'HOC/withOpenIntercom';

import { makeSelectModalProps } from 'reducers/modal/selectors';

import styles from './SubscriptionLimitModal.scss';

const IntercomLink = withOpenIntercom(AnchorLink);

const lockFeature = {
  icon: LockIcon,
  label: 'Added security for your account',
};

const chartsFeature = {
  icon: ChartIcon,
  label: 'Team analytics and insights',
};

const clientsFeature = {
  icon: ClientsMonitor,
  label: 'Analyse your client portfolio',
};

const upsellData = {
  icon: 'capitalise_logo_pro',
  title: 'Build a service line around Capital Advisory',
  subtitle:
    'Do more than refer, we will work with you to build out a service around funding and help you identify when a client needs finance. Speak to one of our team to upgrade your account',
  proSubscriptionFeatures: [
    lockFeature,
    clientsFeature,
    {
      icon: UsersIcon,
      label: '<strong>1</strong> team and <strong>2</strong> users',
    },
    {
      icon: CertificateIcon,
      label: '<strong>CPD</strong> certified online learning',
    },
  ],
};

const subscriptionData = {
  title: 'You need to upgrade your subscription',
  subtitle:
    'Speak to one of our teams to upgrade instantly and add more teams & users to your account.',
  proSubscriptionFeatures: [
    lockFeature,
    chartsFeature,
    {
      icon: UsersIcon,
      label: '<strong>1</strong> more team and <strong>5</strong> more users',
    },
    {
      icon: CertificateIcon,
      label: '<strong>5</strong> more learn licenses',
    },
  ],
};

const SubscriptionLimitModal = ({ modalData }) => {
  const data = modalData?.upsellModal ? upsellData : subscriptionData;
  const { icon, title, subtitle, proSubscriptionFeatures } = data;

  return (
    <Modal contentOnTop={false} contentLabel={title} modalId="subscriptionLimit">
      {(handleClose) => (
        <div className="o-modal-form o-container-xxs u-align-center">
          <div className={styles['o-subscription-limit-modal__main-icon']}>
            {icon ? (
              <Icon icon={icon} className={styles['o-subscription-limit-modal__pro-icon']} />
            ) : (
              <UserWarningIcon className="a-svg-xxxxxlarge" />
            )}
          </div>

          <h1 className={styles['o-subscription-limit-modal__title']}>{title}</h1>

          <p className="o-modal__text">{subtitle}</p>

          <div
            className={`${styles['o-subscription-limit-modal__plan-container']} o-col-block o-col-block--col-collapse`}
          >
            <Chip
              uppercase
              className={styles['o-subscription-limit-modal__chip']}
              label="Starting from"
              medium
              primary
            />

            <div className="o-col-12 o-flexible-col-sml-4 u-align-middle">
              <h5 className={styles['o-subscription-limit-modal__plan--title']}>£ 78</h5>
              per team per month, <br /> billed annually
            </div>

            <div className="o-col-12 o-flexible-col-sml-8 u-align-middle">
              <ul className={styles['o-subscription-limit-modal__list']}>
                {proSubscriptionFeatures.map(({ icon: FeatureIcon, label }, index) => (
                  <li key={index}>
                    <FeatureIcon className={styles['o-subscription-limit-modal__list__icon']} />

                    <RawHtml>{label}</RawHtml>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`o-col-fluid-12 ${styles['o-subscription-limit-modal__contact']}`}>
              <Button
                medium
                primary
                target="_blank"
                label="View pricing plans"
                href="https://capitalise.com/partners/partner-plans"
              />
            </div>
          </div>

          <div
            className={`${styles['o-subscription-limit-modal__banner']} o-col-block o-col-block--col-collapse`}
          >
            <div
              className={`o-flexible-col-2 ${styles['o-subscription-limit-modal__banner--icon-container']} u-align-middle`}
            >
              <BuildingIcon />
            </div>

            <div
              className={`o-flexible-col-10 ${styles['o-subscription-limit-modal__banner--text-container']} u-align-left u-align-middle`}
            >
              <strong>Are you an Enterprise firm?</strong>

              <div>
                <p>You may wish to consider a firm wide licence.</p>

                <p>
                  <IntercomLink>Chat with us</IntercomLink>
                  to find out more.
                </p>
              </div>
            </div>
          </div>

          <div className="o-modal__actions">
            <div className="o-modal__actions__action">
              <AnchorLink onClick={handleClose} secondary>
                No Thanks
              </AnchorLink>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

SubscriptionLimitModal.propTypes = {
  modalData: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  modalData: makeSelectModalProps('subscriptionLimit'),
});

export default connect(mapStateToProps, null)(SubscriptionLimitModal, styles);
